/**
 * Automatic table prefix for plugin entities.
 *
 * Mutates entity definitions in-place, prepending `{code}_` to table names
 * and resolving intra-plugin relation.table references.
 */

import type { EntityDefinition, ModuleManifest } from '@gzl10/nexus-sdk'

/**
 * Prefix all table names in plugin modules with `{code}_`.
 * Also resolves intra-plugin `relation.table` references and DAG `parentsTable`.
 *
 * Mutates modules in-place.
 *
 * @param code - Plugin code (3 lowercase chars)
 * @param modules - Plugin's module manifests
 * @returns Set of original (unprefixed) table names declared by the plugin
 */
export function prefixPluginTables(code: string, modules: ModuleManifest[]): Set<string> {
  const prefix = `${code}_`

  // 1. Collect all table names declared by this plugin (before prefixing)
  const pluginTables = new Set<string>()
  for (const mod of modules) {
    for (const def of mod.definitions ?? []) {
      const table = getTable(def)
      if (table) pluginTables.add(table)
    }
  }

  // 2. Prefix table names, parentsTable, and resolve intra-plugin relation references
  for (const mod of modules) {
    for (const def of mod.definitions ?? []) {
      const table = getTable(def)
      if (!table) continue

      // Prefix table (skip if already prefixed)
      if (!table.startsWith(prefix)) {
        setTable(def, `${prefix}${table}`)
      }

      // Prefix explicit parentsTable for DAG entities (always prefix, no guard)
      const defRecord = def as Record<string, unknown>
      const parentsTable = defRecord['parentsTable'] as string | undefined
      if (parentsTable) {
        defRecord['parentsTable'] = `${prefix}${parentsTable}`
      }

      // Resolve relation.table references
      const fields = (def as { fields?: Record<string, unknown> }).fields
      if (!fields) continue

      for (const field of Object.values(fields)) {
        const relation = (field as { relation?: { table: string } })?.relation
        if (!relation?.table) continue

        // Only prefix if it's an intra-plugin reference
        if (pluginTables.has(relation.table)) {
          relation.table = `${prefix}${relation.table}`
        }
      }
    }
  }

  return pluginTables
}

function getTable(def: EntityDefinition): string | undefined {
  return ('table' in def && typeof def.table === 'string') ? def.table : undefined
}

function setTable(def: EntityDefinition, value: string): void {
  if ('table' in def) {
    (def as { table: string }).table = value
  }
}
